/**
 * Login-Seite – /login
 *
 * Design: Terminal-UI, Dark-Theme, Share Tech Mono.
 * Kein spezifisches Feedback bei falschen Credentials (verhindert User-Enumeration).
 */

import { navigate } from '../router.js';
import { gameState } from '../state/gameState.js';
import { loginUser, resendVerification } from '../services/auth.js';
import { ApiError } from '../services/api.js';

export function renderLogin(): void {
  // Falls bereits eingeloggt: Direkt zum Dashboard umleiten
  if (gameState.isAuthenticated()) {
    navigate('/dashboard');
    return;
  }

  const app = document.getElementById('app');
  if (!app) return;

  // verified=1 / waitlisted=1 / slot=1|expired|invalid Query-Param aus URL lesen:
  // /login?verified=1 · slot=* kommt vom Wartelisten-Einladungslink (NT-PLAYER-CAP-01,
  // GET /api/v1/alpha/invite/:token).
  const loginParams = new URLSearchParams(window.location.search);
  const justVerified = loginParams.get('verified') === '1';
  const justWaitlisted = loginParams.get('waitlisted') === '1';
  const slotResult = loginParams.get('slot');

  app.innerHTML = `
    <main class="auth-page">
      <div class="auth-card">
        <p class="auth-brand">&#9654; NIGHTTRADE</p>
        <h1 class="auth-title">Anmelden</h1>

        ${justVerified ? `
        <div class="form-success" role="status">
          E-Mail erfolgreich verifiziert. Du kannst dich jetzt anmelden.
        </div>` : ''}

        ${justWaitlisted ? `
        <div class="form-success" role="status">
          E-Mail bestätigt. Aktuell sind alle Plätze belegt — melde dich an, um deinen
          Warteliste-Status zu sehen.
        </div>` : ''}

        ${slotResult === '1' ? `
        <div class="form-success" role="status">
          Dein Platz ist da! Melde dich jetzt an.
        </div>` : ''}

        ${slotResult === 'expired' ? `
        <div class="form-error" role="alert">
          Diese Einladung ist leider abgelaufen. Keine Sorge — du bleibst auf der Warteliste
          und bekommst deine nächste Chance, sobald wieder ein Platz frei wird.
        </div>` : ''}

        ${slotResult === 'invalid' ? `
        <div class="form-error" role="alert">
          Dieser Einladungslink ist ungültig oder wurde bereits verwendet.
        </div>` : ''}

        <form id="login-form" novalidate>
          <div class="form-group">
            <label for="login-email">E-Mail-Adresse</label>
            <input
              type="email"
              id="login-email"
              name="email"
              autocomplete="email"
              required
              placeholder="spieler@beispiel.de"
            />
          </div>

          <div class="form-group">
            <label for="login-password">Passwort</label>
            <div class="pw-input-wrap">
              <input
                type="password"
                id="login-password"
                name="password"
                autocomplete="current-password"
                required
              />
              <button type="button" class="pw-toggle-btn" id="login-pw-toggle" aria-label="Passwort anzeigen">ANZEIGEN</button>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" id="login-remember" name="rememberMe" />
              <span>Angemeldet bleiben (7 Tage)</span>
            </label>
          </div>

          <div
            id="login-error"
            class="form-error"
            role="alert"
            aria-live="polite"
            hidden
          ></div>

          <button type="submit" id="login-submit" class="primary btn-full">
            Anmelden
          </button>
        </form>

        <nav class="auth-links">
          <a href="/register">Noch kein Account? &#8594; Registrieren</a>
          <a href="/forgot-password">Passwort vergessen?</a>
        </nav>
      </div>
    </main>
  `;

  const form = document.getElementById('login-form') as HTMLFormElement | null;
  const emailInput = document.getElementById('login-email') as HTMLInputElement | null;
  const passwordInput = document.getElementById('login-password') as HTMLInputElement | null;
  const submitBtn = document.getElementById('login-submit') as HTMLButtonElement | null;
  const errorDiv = document.getElementById('login-error') as HTMLDivElement | null;

  if (!form || !emailInput || !passwordInput || !submitBtn || !errorDiv) return;

  // Passwort-Toggle
  const pwToggle = document.getElementById('login-pw-toggle') as HTMLButtonElement | null;
  pwToggle?.addEventListener('click', (): void => {
    const isHidden = passwordInput.type === 'password';
    passwordInput.type = isHidden ? 'text' : 'password';
    pwToggle.textContent = isHidden ? 'VERBERGEN' : 'ANZEIGEN';
  });

  function showError(message: string): void {
    if (!errorDiv) return;
    errorDiv.textContent = message;
    errorDiv.hidden = false;
  }

  // Eigene, unterscheidbare Meldung bei unverifiziertem Account (NT-REG-FUNNEL-01, Teil B) —
  // NUR erreichbar nach korrekt geprüftem Passwort (siehe Aufrufstelle im submit-Handler),
  // damit diese Unterscheidung nicht zum Account-Enumerations-Werkzeug wird.
  function showUnverifiedError(email: string): void {
    if (!errorDiv) return;
    errorDiv.hidden = false;
    errorDiv.textContent = '';

    const text = document.createElement('p');
    text.style.margin = '0 0 8px';
    text.textContent = 'Bitte bestätige zuerst deine E-Mail-Adresse.';
    errorDiv.appendChild(text);

    const resendBtn = document.createElement('button');
    resendBtn.type = 'button';
    resendBtn.textContent = 'Bestätigungsmail erneut senden';
    errorDiv.appendChild(resendBtn);

    resendBtn.addEventListener('click', () => {
      resendBtn.disabled = true;
      resendBtn.textContent = 'WIRD GESENDET...';
      void (async (): Promise<void> => {
        try {
          await resendVerification(email);
          resendBtn.textContent = 'Erneut gesendet — Postfach prüfen (auch Spam-Ordner)';
        } catch (err: unknown) {
          resendBtn.disabled = false;
          resendBtn.textContent =
            err instanceof ApiError && err.status === 429
              ? 'Zu viele Versuche. Bitte kurz warten.'
              : 'Bestätigungsmail erneut senden';
        }
      })();
    });
  }

  function hideError(): void {
    if (!errorDiv) return;
    errorDiv.textContent = '';
    errorDiv.hidden = true;
  }

  function setLoading(loading: boolean): void {
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    submitBtn.textContent = loading ? '■ WIRD GEPRÜFT...' : 'Anmelden';
    submitBtn.setAttribute('aria-busy', String(loading));
  }

  form.addEventListener('submit', (event: Event): void => {
    event.preventDefault();
    hideError();

    const email = emailInput.value.trim();
    const password = passwordInput.value;

    if (!email) {
      showError('Bitte E-Mail-Adresse eingeben.');
      emailInput.focus();
      return;
    }

    if (!password) {
      showError('Bitte Passwort eingeben.');
      passwordInput.focus();
      return;
    }

    setLoading(true);

    void (async (): Promise<void> => {
      try {
        const result = await loginUser(email, password);
        // NT-ALPHA-REWORK-01: Warteliste-Spieler dürfen einloggen, kommen aber zur Warteliste-Seite
        navigate(result.accessStatus === 'WAITLIST' ? '/warteliste' : '/dashboard');
      } catch (err: unknown) {
        setLoading(false);
        if (err instanceof ApiError) {
          if (err.status === 429 || err.code === 'ACCOUNT_LOCKED') {
            showError('Zu viele Versuche. Bitte 15 Minuten warten.');
          } else if (err.code === 'EMAIL_NOT_VERIFIED') {
            showUnverifiedError(email);
          } else {
            // Bewusst kein spezifisches Feedback (verhindert User-Enumeration)
            showError('E-Mail oder Passwort falsch.');
          }
        } else {
          showError('Verbindungsfehler. Bitte versuche es erneut.');
        }
      }
    })();
  });
}
